import { useState } from 'react';
import {
  Hash,
  Plus,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Edit2,
  Save,
  X,
  AlertCircle,
  Loader,
} from 'lucide-react';
import { useProfile } from '@/hooks/useProfile';
import {
  useKeywords,
  useCreateKeyword,
  useUpdateKeyword,
  useDeleteKeyword,
} from '@/hooks/useAccountKeywords';
import { AccountKeyword } from '@/types/accountKeywords';
import { useConfirm } from '@/hooks/useConfirm';
import { useUserProfile } from '@/hooks/useUserProfile';
import { DateInput } from '@/components/ui/DateInput';
import { useDateFormatter } from '@/hooks/useDateFormatter';
import { useDemoModeSettings } from '@/hooks/useDemoModeSettings';
import { DemoTooltipWrapper } from '@/components/ui/DemoTooltipWrapper';
import { cn } from '@/lib/utils';
import { useVersionTheme } from '@/hooks/useVersionTheme';

/**
 * Shared Keywords Settings Component
 *
 * Works in V11 using the version-aware theme hook.
 * - V9/V9.5: Always dark mode
 * - V11: Respects light/dark theme toggle
 */
export const KeywordsSettings = () => {
  const theme = useVersionTheme();

  // Demo mode support
  const { isDemoMode, showDemoToast } = useDemoModeSettings();

  // Confirm dialog
  const { confirm, ConfirmDialog } = useConfirm();

  // Get user profile to extract account_id
  const { data: profile, isLoading: isLoadingProfile, error: profileError } = useProfile();

  // Get refetchProfile to refresh metrics after updates
  const { refetchProfile } = useUserProfile();

  // Date formatter with user's timezone
  const { formatDate } = useDateFormatter();

  // Get account ID (first account)
  const accountId = profile?.accountHealth?.accountId;

  // Fetch keywords
  const {
    data: keywords = [],
    isLoading: isLoadingKeywords,
    error: keywordsError,
  } = useKeywords({ account_id: accountId }, { enabled: !!accountId });

  // Mutations
  const createMutation = useCreateKeyword();
  const updateMutation = useUpdateKeyword();
  const deleteMutation = useDeleteKeyword();

  // Form state
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newKeyword, setNewKeyword] = useState({
    keyword: '',
    analysisSince: '',
    isActive: true,
  });
  const [validationError, setValidationError] = useState('');

  // Edit mode state
  const [editingKeyword, setEditingKeyword] = useState<number | null>(null);
  const [editForm, setEditForm] = useState({
    analysis_since: '',
  });

  const startEditing = (keyword: AccountKeyword) => {
    setEditingKeyword(keyword.id);
    const dateValue = keyword.analysis_since ? keyword.analysis_since.split('T')[0] : '';
    setEditForm({
      analysis_since: dateValue,
    });
  };

  const cancelEditing = () => {
    setEditingKeyword(null);
    setEditForm({ analysis_since: '' });
  };

  const saveEditing = async (keyword: AccountKeyword) => {
    try {
      await updateMutation.mutateAsync({
        id: keyword.id,
        data: {
          is_active: keyword.is_active,
          analysis_since: editForm.analysis_since || undefined,
        },
      });
      setEditingKeyword(null);
      // Refresh user profile to update metrics
      await refetchProfile();
    } catch (error) {
      console.error('Failed to update keyword:', error);
    }
  };

  const handleToggleActive = async (keyword: AccountKeyword) => {
    try {
      await updateMutation.mutateAsync({
        id: keyword.id,
        data: {
          is_active: !keyword.is_active,
          analysis_since: keyword.analysis_since || undefined,
        },
      });
      // Refresh user profile to update metrics
      await refetchProfile();
    } catch (error) {
      console.error('Failed to toggle keyword status:', error);
    }
  };

  const handleDelete = async (keywordId: number, keywordText: string) => {
    const confirmed = await confirm({
      title: 'Delete Keyword',
      description: `Are you sure you want to delete keyword "${keywordText}"?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });

    if (!confirmed) return;

    try {
      await deleteMutation.mutateAsync(keywordId);
      // Refresh user profile to update metrics
      await refetchProfile();
    } catch (error) {
      console.error('Failed to delete keyword:', error);
    }
  };

  const handleSubmitNew = async () => {
    setValidationError('');

    if (!accountId) {
      setValidationError('Account ID not found');
      return;
    }

    if (!newKeyword.keyword.trim()) {
      setValidationError('Keyword is required');
      return;
    }

    try {
      await createMutation.mutateAsync({
        account_id: accountId,
        keyword: newKeyword.keyword.trim(),
        is_active: newKeyword.isActive,
        analysis_since: newKeyword.analysisSince || undefined,
      });

      // Reset form
      setNewKeyword({
        keyword: '',
        analysisSince: '',
        isActive: true,
      });
      setIsAddingNew(false);
      // Refresh user profile to update metrics
      await refetchProfile();
    } catch (error: any) {
      console.error('Failed to create keyword:', error);
      setValidationError(
        error.response?.data?.message || 'Failed to create keyword. Please try again.'
      );
    }
  };

  // Loading state
  if (isLoadingProfile || (isLoadingKeywords && !keywords.length)) {
    return (
      <div className={`min-h-screen ${theme.bg} ${theme.textPrimary} p-6`}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <Loader className="h-8 w-8 animate-spin text-blue-400" />
            <span className={`ml-3 ${theme.textMuted}`}>Loading keywords...</span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (profileError || !accountId) {
    return (
      <div className={`min-h-screen ${theme.bg} ${theme.textPrimary} p-6`}>
        <div className="max-w-7xl mx-auto">
          <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
              <div>
                <p className={`font-semibold ${theme.textPrimary}`}>Failed to load account</p>
                <p className="text-sm text-red-400 mt-1">
                  {profileError?.message || 'No account found. Please contact support.'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className={`text-2xl font-bold ${theme.textPrimary}`}>Keywords Management</h2>
          <p className={`text-sm ${theme.textMuted} mt-1`}>
            Track keywords and brand mentions across social media
          </p>
        </div>
        {!isAddingNew && (
          <DemoTooltipWrapper enabled={isDemoMode}>
            <button
              onClick={isDemoMode ? showDemoToast : () => setIsAddingNew(true)}
              disabled={isDemoMode || keywords.length >= 100}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-400 text-white font-medium transition disabled:opacity-50 disabled:cursor-not-allowed",
                isDemoMode && "opacity-50 cursor-not-allowed"
              )}
              title={keywords.length >= 100 ? 'Maximum 100 keywords reached' : 'Add keyword'}
            >
              <Plus className="h-4 w-4" />
              Add Keyword
            </button>
          </DemoTooltipWrapper>
        )}
      </div>

      {/* Add New Keyword Form */}
      {isAddingNew && (
        <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className={`text-lg font-semibold ${theme.textPrimary}`}>Add New Keyword</h3>
            <button
              onClick={() => {
                setIsAddingNew(false);
                setValidationError('');
                setNewKeyword({
                  keyword: '',
                  analysisSince: '',
                  isActive: true,
                });
              }}
              className={`text-sm ${theme.textMuted} hover:${theme.textPrimary} transition`}
            >
              Cancel
            </button>
          </div>

          <div className="space-y-4">
            {/* Keyword */}
            <div>
              <label className={`block text-sm font-medium ${theme.textSecondary} mb-2`}>
                Keyword *
              </label>
              <DemoTooltipWrapper enabled={isDemoMode}>
                <div className="relative">
                  <Hash className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${theme.textMuted}`} />
                  <input
                    type="text"
                    value={newKeyword.keyword}
                    onChange={e => setNewKeyword({ ...newKeyword, keyword: e.target.value })}
                    maxLength={255}
                    disabled={isDemoMode}
                    className={cn(
                      `w-full pl-10 pr-4 py-3 rounded-lg border ${theme.border} ${theme.surfaceHover} ${theme.textPrimary} placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500`,
                      isDemoMode && "opacity-70 cursor-not-allowed"
                    )}
                  />
                </div>
              </DemoTooltipWrapper>
              <p className={`text-xs ${theme.textTertiary} mt-1`}>
                Enter a keyword or brand name to track (max 255 characters)
              </p>
            </div>

            {/* Analysis Start Date */}
            <DemoTooltipWrapper enabled={isDemoMode}>
              <DateInput
                label="Analysis Start Date *"
                value={newKeyword.analysisSince}
                onChange={e => setNewKeyword({ ...newKeyword, analysisSince: e.target.value })}
                max={new Date().toISOString().split('T')[0]}
                helperText="Data will be collected from this date onwards"
                disabled={isDemoMode}
                className={isDemoMode ? "opacity-70 cursor-not-allowed" : ""}
              />
            </DemoTooltipWrapper>

            {/* Active Monitoring */}
            <div className={`flex items-start gap-3 p-4 rounded-lg ${theme.surfaceHover} border ${theme.border}`}>
              <DemoTooltipWrapper enabled={isDemoMode}>
                <button
                  onClick={isDemoMode ? showDemoToast : () =>
                    setNewKeyword({
                      ...newKeyword,
                      isActive: !newKeyword.isActive,
                    })
                  }
                  disabled={isDemoMode}
                  className={cn(
                    "flex-shrink-0 mt-0.5",
                    isDemoMode && "opacity-50 cursor-not-allowed"
                  )}
                >
                  {newKeyword.isActive ? (
                    <ToggleRight className="h-6 w-6 text-green-400" />
                  ) : (
                    <ToggleLeft className={`h-6 w-6 ${theme.textMuted}`} />
                  )}
                </button>
              </DemoTooltipWrapper>
              <div>
                <p className={`text-sm font-medium ${theme.textPrimary}`}>Active Monitoring</p>
                <p className={`text-xs ${theme.textMuted} mt-1`}>
                  Start monitoring immediately. You can pause this anytime.
                </p>
              </div>
            </div>

            {/* Validation Error */}
            {validationError && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                <p className="text-sm text-red-400">{validationError}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-3 pt-2">
              <DemoTooltipWrapper enabled={isDemoMode}>
                <button
                  onClick={isDemoMode ? showDemoToast : handleSubmitNew}
                  disabled={isDemoMode || createMutation.isPending}
                  className={cn(
                    "flex items-center gap-2 px-6 py-3 rounded-lg bg-blue-500 hover:bg-blue-400 text-white font-medium transition disabled:opacity-50 disabled:cursor-not-allowed",
                    isDemoMode && "opacity-50 cursor-not-allowed"
                  )}
                >
                  {createMutation.isPending ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      Add Keyword
                    </>
                  )}
                </button>
              </DemoTooltipWrapper>
            </div>
          </div>
        </div>
      )}

      {/* Keywords List */}
      {keywordsError && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-6">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <p className="text-sm text-red-400">Failed to load keywords. Please try again.</p>
          </div>
        </div>
      )}

      {keywords.length === 0 && !isAddingNew ? (
        <div className={`rounded-xl border ${theme.border} ${theme.surface} p-12 text-center`}>
          <Hash className={`h-12 w-12 ${theme.textTertiary} mx-auto mb-4`} />
          <h3 className={`text-lg font-semibold ${theme.textPrimary} mb-2`}>No keywords yet</h3>
          <p className={`${theme.textMuted} mb-6`}>
            Start by adding your first keyword or brand to track
          </p>
          <DemoTooltipWrapper enabled={isDemoMode}>
            <button
              onClick={isDemoMode ? showDemoToast : () => setIsAddingNew(true)}
              disabled={isDemoMode}
              className={cn(
                "inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-400 text-white font-medium transition",
                isDemoMode && "opacity-50 cursor-not-allowed"
              )}
            >
              <Plus className="h-4 w-4" />
              Add Your First Keyword
            </button>
          </DemoTooltipWrapper>
        </div>
      ) : (
        <div className="space-y-4">
          {keywords.map(keyword => (
            <div
              key={keyword.id}
              className={`rounded-xl border ${theme.border} ${theme.surface} p-6 hover:border-${theme.border} transition`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-4 flex-1">
                  {/* Keyword Icon */}
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                    <Hash className="h-6 w-6 text-white" />
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2 flex-wrap">
                      <h3 className={`text-lg font-semibold ${theme.textPrimary}`}>{keyword.keyword}</h3>
                    </div>

                    {/* Edit mode */}
                    {editingKeyword === keyword.id ? (
                      <div className="space-y-3 mt-3">
                        <DemoTooltipWrapper enabled={isDemoMode}>
                          <DateInput
                            label="Analysis Since"
                            value={editForm.analysis_since}
                            onChange={e =>
                              setEditForm({ ...editForm, analysis_since: e.target.value })
                            }
                            max={new Date().toISOString().split('T')[0]}
                            className="text-sm py-2"
                            disabled={isDemoMode}
                          />
                        </DemoTooltipWrapper>

                        <div className="flex items-center gap-2">
                          <DemoTooltipWrapper enabled={isDemoMode}>
                            <button
                              onClick={isDemoMode ? showDemoToast : () => saveEditing(keyword)}
                              disabled={isDemoMode || updateMutation.isPending}
                              className={cn(
                                "flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-400 text-white text-sm font-medium transition disabled:opacity-50",
                                isDemoMode && "opacity-50 cursor-not-allowed"
                              )}
                            >
                              {updateMutation.isPending ? (
                                <>
                                  <Loader className="h-4 w-4 animate-spin" />
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <Save className="h-4 w-4" />
                                  Save
                                </>
                              )}
                            </button>
                          </DemoTooltipWrapper>
                          <button
                            onClick={cancelEditing}
                            disabled={updateMutation.isPending}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${theme.border} ${theme.textSecondary} hover:${theme.surfaceHover} text-sm transition disabled:opacity-50`}
                          >
                            <X className="h-4 w-4" />
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Inline stats */}
                        <div className={`flex items-center gap-4 text-sm ${theme.textSecondary} flex-wrap`}>
                          <span className={theme.textMuted}>Analysis Since</span>
                          <span className={`font-medium ${theme.textPrimary}`}>
                            {formatDate(keyword.analysis_since, 'dd/MM/yyyy')}
                          </span>
                          <span className={theme.textTertiary}>•</span>
                          <span className={theme.textMuted}>Added</span>
                          <span className={`font-medium ${theme.textPrimary}`}>
                            {formatDate(keyword.created_at, 'dd/MM/yyyy')}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Action Buttons */}
                {editingKeyword !== keyword.id && (
                  <div className="flex items-center gap-2">
                    <DemoTooltipWrapper enabled={isDemoMode}>
                      <button
                        onClick={isDemoMode ? showDemoToast : () => handleToggleActive(keyword)}
                        disabled={isDemoMode || updateMutation.isPending}
                        className={cn(
                          `p-2 rounded-lg border transition disabled:opacity-50 disabled:cursor-not-allowed ${
                            keyword.is_active
                              ? 'border-green-500/30 bg-green-500/10 text-green-400 hover:bg-green-500/20'
                              : `border-${theme.border} ${theme.surfaceHover} ${theme.textMuted} hover:${theme.surfaceHover}`
                          }`,
                          isDemoMode && "opacity-50 cursor-not-allowed"
                        )}
                        title={keyword.is_active ? 'Pause monitoring' : 'Resume monitoring'}
                      >
                        {updateMutation.isPending ? (
                          <Loader className="h-5 w-5 animate-spin" />
                        ) : keyword.is_active ? (
                          <ToggleRight className="h-5 w-5" />
                        ) : (
                          <ToggleLeft className="h-5 w-5" />
                        )}
                      </button>
                    </DemoTooltipWrapper>

                    <DemoTooltipWrapper enabled={isDemoMode}>
                      <button
                        onClick={isDemoMode ? showDemoToast : () => startEditing(keyword)}
                        disabled={isDemoMode || editingKeyword !== null}
                        className={cn(
                          "p-2 rounded-lg border border-blue-500/30 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition disabled:opacity-50 disabled:cursor-not-allowed",
                          isDemoMode && "opacity-50 cursor-not-allowed"
                        )}
                        title="Edit date"
                      >
                        <Edit2 className="h-5 w-5" />
                      </button>
                    </DemoTooltipWrapper>

                    <DemoTooltipWrapper enabled={isDemoMode}>
                      <button
                        onClick={isDemoMode ? showDemoToast : () => handleDelete(keyword.id, keyword.keyword)}
                        disabled={isDemoMode || deleteMutation.isPending || editingKeyword !== null}
                        className={cn(
                          "p-2 rounded-lg border border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20 transition disabled:opacity-50 disabled:cursor-not-allowed",
                          isDemoMode && "opacity-50 cursor-not-allowed"
                        )}
                        title="Remove keyword"
                      >
                        {deleteMutation.isPending ? (
                          <Loader className="h-5 w-5 animate-spin" />
                        ) : (
                          <Trash2 className="h-5 w-5" />
                        )}
                      </button>
                    </DemoTooltipWrapper>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <ConfirmDialog />
    </div>
  );
};

export default KeywordsSettings;
